<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePreferredDark } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import AppearanceSchemeGallery from './AppearanceSchemeGallery.vue';
import AppearanceWallpaperPicker from './AppearanceWallpaperPicker.vue';
import AppearanceAccentColorPicker from './AppearanceAccentColorPicker.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme, WALLPAPER_OPTIONS } from 'dashboard/helper/themeHelper';
import {
  getLocalConversationWallpaper,
  removeLocalConversationWallpaper,
  saveLocalConversationWallpaper,
} from 'dashboard/helper/localWallpaperHelper';
import { normalizePrimaryColor } from 'dashboard/helper/primaryColorHelper';
import { emitter } from 'shared/helpers/mitt';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  DASHBOARD_FONT_OPTIONS,
  DASHBOARD_FONT_WEIGHT_OPTIONS,
  initializeDashboardFont,
  initializeDashboardFontWeight,
  normalizeDashboardFont,
  normalizeDashboardFontWeight,
} from 'dashboard/helper/fontHelper';
import {
  DASHBOARD_CONTAINERS,
  DASHBOARD_LAYOUTS,
  normalizeDashboardContainer,
  normalizeDashboardLayout,
} from 'dashboard/composables/useDashboardLayout';

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'customizeNavigation']);
const { uiSettings, updateUISettings } = useUISettings();
const { fontSizeOptions, currentFontSize, updateFontSize } = useFontSize();
const contactPreviewExpanded = ref(false);
watch(
  () => [uiSettings.value?.conversation_list_compact, props.show],
  () => {
    contactPreviewExpanded.value = false;
  }
);

const copy = {
  contactMenuTitle: 'Menu dos contatos',
  contactMenuDescription:
    'No modo compacto, mostre apenas nome e mensagem. Use Ver mais em cada contato para abrir as demais informações.',
  contactPreviewTitle: 'Exemplo com dados fictícios',
  contactPreviewName: 'Tom Martins',
  contactPreviewMessage: 'Olá! Gostaria de saber mais sobre a proposta.',
  contactPreviewInbox: 'Atendimento WhatsApp',
  contactPreviewAssignee: 'Equipe comercial',
  contactPreviewLabel: 'Cliente interessado',
  contactPreviewMore: 'Ver mais',
  contactPreviewLess: 'Ver menos',
  eyebrow: 'Personalização',
  title: 'Aparência',
  layoutTitle: 'Navegação',
  navigationTitle: 'Menu de navegação',
  navigationDescription:
    'Organize os atalhos, escolha seus favoritos e recolha seções.',
  customizeNavigation: 'Personalizar navegação',
  layoutDescription: 'Escolha como o menu principal organiza o Dashboard.',
  containerTitle: 'Largura do conteúdo',
  containerDescription:
    'Use toda a tela ou mantenha o conteúdo centralizado e mais compacto.',
  horizontalResponsiveNote:
    'O menu horizontal é aplicado em telas grandes. Em telas menores, a navegação lateral móvel continua ativa.',
  colorModeTitle: 'Tom do sistema',
  colorModeDescription:
    'Troque imediatamente entre claro, escuro ou acompanhe o sistema operacional.',
  fontTitle: 'Fonte do sistema',
  fontDescription: 'Escolha a tipografia usada em todo o Dashboard.',
  fontSample: 'Aa',
  fontWeightTitle: 'Espessura da fonte',
  fontWeightDescription:
    'Escolha o peso padrão dos textos sem alterar títulos e destaques.',
  fontSizeTitle: 'Tamanho da fonte',
  fontSizeDescription:
    'Ajuste o tamanho dos textos em todo o Dashboard conforme sua preferência.',
  darkSchemeTitle: 'Tom escuro',
  darkSchemeDescription:
    'Aparência do fundo quando o modo escuro estiver ativo.',
  lightSchemeTitle: 'Tom claro',
  lightSchemeDescription:
    'Aparência das superfícies quando o modo claro estiver ativo.',
  darkSchemeOptions: 'opções',
  wallpaperTitle: 'Papel de parede',
  wallpaperDescription: 'Cor de fundo da área de conversa.',
  wallpaperInvalidType: 'Escolha uma imagem JPG, PNG ou WebP.',
  wallpaperTooLarge: 'A imagem deve ter no máximo 5 MB.',
  wallpaperSaveError: 'Não foi possível salvar a imagem neste navegador.',
  notificationsTitle: 'Notificações',
  notificationsDescription: 'Como e onde os avisos aparecem na tela.',
  notificationPositionTitle: 'Posição na tela',
  notificationPositionDescription:
    'Escolha onde os avisos devem aparecer sem atrapalhar seu atendimento.',
  previewNotification: 'Testar notificação',
  previewMessage: 'Prévia de notificação: este é o modelo selecionado.',
  navigationLabel: 'Seções de aparência',
  close: 'Fechar',
};

const lightSchemes = [
  {
    key: 'default',
    label: 'Padrão',
    description: 'Claro neutro',
    bg: '#f7f7f7',
    sidebar: '#fefefe',
    surface: '#ffffff',
    accent: '#ececef',
  },
  {
    key: 'snow',
    label: 'Neve',
    description: 'Branco frio',
    bg: '#f8fafc',
    sidebar: '#fcfdff',
    surface: '#ffffff',
    accent: '#e8edf3',
  },
  {
    key: 'ivory',
    label: 'Marfim',
    description: 'Branco acolhedor',
    bg: '#faf7f2',
    sidebar: '#fffcf7',
    surface: '#ffffff',
    accent: '#f1eae0',
  },
  {
    key: 'mint',
    label: 'Menta',
    description: 'Verde suave',
    bg: '#f2faf7',
    sidebar: '#f8fdfb',
    surface: '#ffffff',
    accent: '#e0f0ea',
  },
  {
    key: 'sky',
    label: 'Céu',
    description: 'Azul suave',
    bg: '#f3f8fd',
    sidebar: '#f9fcff',
    surface: '#ffffff',
    accent: '#e1ecf7',
  },
  {
    key: 'lavender',
    label: 'Lavanda',
    description: 'Roxo delicado',
    bg: '#f8f6fd',
    sidebar: '#fcfaff',
    surface: '#ffffff',
    accent: '#ede6f7',
  },
  {
    key: 'rose',
    label: 'Rosé',
    description: 'Rosa discreto',
    bg: '#fdf6f8',
    sidebar: '#fffbfc',
    surface: '#ffffff',
    accent: '#f8e7ed',
  },
  {
    key: 'sand',
    label: 'Areia',
    description: 'Bege natural',
    bg: '#f9f6ee',
    sidebar: '#fefbf4',
    surface: '#ffffff',
    accent: '#f1e9d6',
  },
].map(scheme => ({ ...scheme, ink: '#334155' }));

const darkSchemes = [
  {
    key: 'default',
    label: 'Padrão',
    description: 'Neutro equilibrado',
    bg: '#121213',
    sidebar: '#111113',
    surface: '#17171a',
    accent: '#1d1e24',
  },
  {
    key: 'mint',
    label: 'Mint',
    description: 'Verde profundo',
    bg: '#0e1d21',
    sidebar: '#0d1a1d',
    surface: '#11262b',
    accent: '#1a353c',
  },
  {
    key: 'navy',
    label: 'Navy',
    description: 'Azul corporativo',
    bg: '#1f2d46',
    sidebar: '#172236',
    surface: '#1f2d46',
    accent: '#273958',
  },
  {
    key: 'mirage',
    label: 'Mirage',
    description: 'Azul noturno',
    bg: '#121422',
    sidebar: '#0c1422',
    surface: '#121d30',
    accent: '#192d48',
  },
  {
    key: 'cinder',
    label: 'Cinder',
    description: 'Cinza acolhedor',
    bg: '#1c1c20',
    sidebar: '#141417',
    surface: '#1c1c20',
    accent: '#2b2b30',
  },
  {
    key: 'black',
    label: 'Black',
    description: 'Preto absoluto',
    bg: '#0d0d0f',
    sidebar: '#080809',
    surface: '#0d0d0f',
    accent: '#1a1a1e',
  },
  {
    key: 'forest',
    label: 'Floresta',
    description: 'Verde natural',
    bg: '#091610',
    sidebar: '#0c1c15',
    surface: '#11281e',
    accent: '#1a3a2b',
  },
  {
    key: 'plum',
    label: 'Ameixa',
    description: 'Roxo sofisticado',
    bg: '#180e1c',
    sidebar: '#1d1122',
    surface: '#2c1934',
    accent: '#41254d',
  },
  {
    key: 'mocha',
    label: 'Café',
    description: 'Marrom confortável',
    bg: '#1b1410',
    sidebar: '#211813',
    surface: '#30231b',
    accent: '#443227',
  },
  {
    key: 'graphite',
    label: 'Grafite',
    description: 'Cinza azulado',
    bg: '#0e1317',
    sidebar: '#11171c',
    surface: '#192229',
    accent: '#26333d',
  },
  {
    key: 'burgundy',
    label: 'Bordô',
    description: 'Vermelho profundo',
    bg: '#1b0d12',
    sidebar: '#211016',
    surface: '#311720',
    accent: '#48222e',
  },
  {
    key: 'olive',
    label: 'Oliva',
    description: 'Terroso discreto',
    bg: '#15160c',
    sidebar: '#1a1b0f',
    surface: '#272915',
    accent: '#3a3d1f',
  },
].map(scheme => ({ ...scheme, ink: '#ffffff' }));

const wallpaperOptions = [
  ['none', 'Padrão'],
  ['whatsapp', 'Verde WhatsApp'],
  ['beige', 'Areia'],
  ['sky', 'Azul'],
  ['lavender', 'Lavanda'],
  ['rose', 'Rosa'],
  ['mist', 'Névoa'],
  ['mint', 'Menta'],
  ['peach', 'Pêssego'],
  ['graphite', 'Grafite'],
  ['aurora', 'Aurora'],
  ['sunset', 'Entardecer'],
  ['dark_pattern', 'WA Escuro'],
  ['classic', 'WA Claro'],
].map(([key, label]) => ({ key, label, ...WALLPAPER_OPTIONS[key] }));

const notificationStyles = [
  {
    key: 'stacked',
    label: 'Empilhadas',
    description: 'Compactas, uma sobre a outra.',
    icon: 'i-lucide-layers-3',
  },
  {
    key: 'expanded',
    label: 'Expandidas',
    description: 'Separadas e mais fáceis de ler.',
    icon: 'i-lucide-list',
  },
];

const notificationPositions = [
  {
    key: 'top-left',
    label: 'Superior esquerdo',
    icon: 'i-lucide-arrow-up-left',
  },
  { key: 'top-center', label: 'Superior centro', icon: 'i-lucide-arrow-up' },
  {
    key: 'top-right',
    label: 'Superior direito',
    icon: 'i-lucide-arrow-up-right',
  },
  { key: 'middle-left', label: 'Centro esquerdo', icon: 'i-lucide-arrow-left' },
  { key: 'center', label: 'Centro', icon: 'i-lucide-scan' },
  {
    key: 'middle-right',
    label: 'Centro direito',
    icon: 'i-lucide-arrow-right',
  },
  {
    key: 'bottom-left',
    label: 'Inferior esquerdo',
    icon: 'i-lucide-arrow-down-left',
  },
  {
    key: 'bottom-center',
    label: 'Inferior centro',
    icon: 'i-lucide-arrow-down',
  },
  {
    key: 'bottom-right',
    label: 'Inferior direito',
    icon: 'i-lucide-arrow-down-right',
  },
];

const selectedPrimaryColor = ref(
  normalizePrimaryColor(LocalStorage.get(LOCAL_STORAGE_KEYS.PRIMARY_COLOR))
);
const selectedColorScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);
const prefersDark = usePreferredDark();
const isDarkMode = computed(() =>
  selectedColorScheme.value === 'auto'
    ? prefersDark.value
    : selectedColorScheme.value === 'dark'
);
const contentRef = ref(null);
const resetContentScroll = async () => {
  await nextTick();
  if (contentRef.value) contentRef.value.scrollTop = 0;
};

const selectedLightScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.LIGHT_COLOR_SCHEME) || 'default'
);
const selectedDarkScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.DARK_COLOR_SCHEME) || 'default'
);
const selectedNotificationStyle = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_GROUP_STYLE) || 'expanded'
);
const selectedNotificationPosition = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.NOTIFICATION_POSITION) || 'top-right'
);
const selectedWallpaper = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER) || 'none'
);
const customWallpaperUrl = ref('');
const customWallpaperName = ref('');
const customWallpaperError = ref('');
const isSavingCustomWallpaper = ref(false);
const selectedDashboardFont = ref(
  normalizeDashboardFont(
    uiSettings.value?.dashboard_font ||
      LocalStorage.get(LOCAL_STORAGE_KEYS.DASHBOARD_FONT)
  )
);
const selectedDashboardFontWeight = ref(
  normalizeDashboardFontWeight(
    uiSettings.value?.dashboard_font_weight ||
      LocalStorage.get(LOCAL_STORAGE_KEYS.DASHBOARD_FONT_WEIGHT)
  )
);
const selectedDashboardFontSize = ref(currentFontSize.value);
const selectedDashboardLayout = ref(
  normalizeDashboardLayout(uiSettings.value?.dashboard_layout)
);
const selectedDashboardContainer = ref(
  normalizeDashboardContainer(uiSettings.value?.dashboard_container)
);
const activePanel = ref('layout');

const appearancePanels = [
  { key: 'layout', label: 'Layout', icon: 'i-lucide-panels-top-left' },
  { key: 'fonts', label: 'Fontes', icon: 'i-lucide-type' },
  { key: 'tones', label: 'Tons', icon: 'i-lucide-palette' },
  { key: 'conversation', label: 'Conversa', icon: 'i-lucide-message-square' },
  { key: 'notifications', label: 'Avisos', icon: 'i-lucide-bell-ring' },
];

const colorSchemeOptions = [
  { key: 'light', label: 'Claro', icon: 'i-lucide-sun' },
  { key: 'dark', label: 'Escuro', icon: 'i-lucide-moon' },
  { key: 'auto', label: 'Sistema', icon: 'i-lucide-monitor' },
];

const dashboardLayoutOptions = [
  {
    key: DASHBOARD_LAYOUTS.VERTICAL,
    label: 'Vertical',
    description: 'Menu lateral redimensionável.',
    icon: 'i-lucide-panel-left',
  },
  {
    key: DASHBOARD_LAYOUTS.HORIZONTAL,
    label: 'Horizontal',
    description: 'Topbar com menu principal no topo.',
    icon: 'i-lucide-panel-top',
  },
];

const dashboardContainerOptions = [
  {
    key: DASHBOARD_CONTAINERS.FULL,
    label: 'Completa',
    description: 'Aproveita toda a largura disponível.',
    icon: 'i-lucide-maximize-2',
  },
  {
    key: DASHBOARD_CONTAINERS.BOXED,
    label: 'Centralizada',
    description: 'Limita o conteúdo a 1440 px.',
    icon: 'i-lucide-square-dashed',
  },
];

let themeTransitionAnimation = null;
const startThemeTransition = () => {
  themeTransitionAnimation?.cancel();
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    typeof document.body.animate !== 'function'
  ) {
    return;
  }

  themeTransitionAnimation = document.body.animate(
    [
      { opacity: 0.94, filter: 'brightness(0.98)' },
      { opacity: 1, filter: 'brightness(1)' },
    ],
    { duration: 240, easing: 'ease-out' }
  );
};

const applyPreferences = () => {
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.PRIMARY_COLOR,
    selectedPrimaryColor.value
  );
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, selectedColorScheme.value);
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.LIGHT_COLOR_SCHEME,
    selectedLightScheme.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.DARK_COLOR_SCHEME,
    selectedDarkScheme.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.NOTIFICATION_GROUP_STYLE,
    selectedNotificationStyle.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.NOTIFICATION_POSITION,
    selectedNotificationPosition.value
  );
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.CONVERSATION_WALLPAPER,
    selectedWallpaper.value
  );
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};

watch(
  [
    selectedPrimaryColor,
    selectedColorScheme,
    selectedLightScheme,
    selectedDarkScheme,
    selectedNotificationStyle,
    selectedNotificationPosition,
    selectedWallpaper,
  ],
  applyPreferences
);
watch(activePanel, resetContentScroll);
watch(selectedColorScheme, startThemeTransition, { flush: 'post' });
watch(currentFontSize, value => {
  selectedDashboardFontSize.value = value;
});
watch([selectedDashboardLayout, selectedDashboardContainer], () => {
  updateUISettings({
    dashboard_layout: selectedDashboardLayout.value,
    dashboard_container: selectedDashboardContainer.value,
  });
});

let previousBodyOverflow;
let previousDocumentOverflow;
const restorePageScroll = () => {
  if (previousBodyOverflow === undefined) return;

  document.body.style.overflow = previousBodyOverflow;
  document.documentElement.style.overflow = previousDocumentOverflow;
  previousBodyOverflow = undefined;
  previousDocumentOverflow = undefined;
};

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) {
      restorePageScroll();
      return;
    }

    selectedColorScheme.value =
      LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto';
    previousBodyOverflow = document.body.style.overflow;
    previousDocumentOverflow = document.documentElement.style.overflow;
    resetContentScroll();
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  },
  { immediate: true }
);

let customWallpaperPreviewUrl;
const setCustomWallpaperPreview = asset => {
  if (customWallpaperPreviewUrl) URL.revokeObjectURL(customWallpaperPreviewUrl);

  if (!asset?.blob) {
    customWallpaperPreviewUrl = undefined;
    customWallpaperUrl.value = '';
    customWallpaperName.value = '';
    return;
  }

  customWallpaperPreviewUrl = URL.createObjectURL(asset.blob);
  customWallpaperUrl.value = customWallpaperPreviewUrl;
  customWallpaperName.value = asset.name || '';
};

const loadCustomWallpaper = async () => {
  try {
    setCustomWallpaperPreview(await getLocalConversationWallpaper());
  } catch {
    setCustomWallpaperPreview(null);
  }
};

const handleWallpaperUpload = async file => {
  customWallpaperError.value = '';
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    customWallpaperError.value = copy.wallpaperInvalidType;
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    customWallpaperError.value = copy.wallpaperTooLarge;
    return;
  }

  isSavingCustomWallpaper.value = true;
  try {
    await saveLocalConversationWallpaper(file);
    await loadCustomWallpaper();
    selectedWallpaper.value = 'custom';
  } catch {
    customWallpaperError.value = copy.wallpaperSaveError;
  } finally {
    isSavingCustomWallpaper.value = false;
  }
};

const removeCustomWallpaper = async () => {
  isSavingCustomWallpaper.value = true;
  customWallpaperError.value = '';
  try {
    await removeLocalConversationWallpaper();
    setCustomWallpaperPreview(null);
    if (selectedWallpaper.value === 'custom') selectedWallpaper.value = 'none';
  } catch {
    customWallpaperError.value = copy.wallpaperSaveError;
  } finally {
    isSavingCustomWallpaper.value = false;
  }
};

onMounted(loadCustomWallpaper);

onUnmounted(() => {
  restorePageScroll();
  themeTransitionAnimation?.cancel();
  if (customWallpaperPreviewUrl) URL.revokeObjectURL(customWallpaperPreviewUrl);
});

const close = () => emit('close');
const customizeNavigation = () => {
  close();
  emit('customizeNavigation');
};
const selectLightScheme = scheme => {
  selectedLightScheme.value = scheme;
};
const selectDarkScheme = scheme => {
  selectedDarkScheme.value = scheme;
};
const selectDashboardFont = font => {
  selectedDashboardFont.value = initializeDashboardFont(font);
  updateUISettings({ dashboard_font: selectedDashboardFont.value });
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};
const selectDashboardFontWeight = weight => {
  selectedDashboardFontWeight.value = initializeDashboardFontWeight(weight);
  updateUISettings({
    dashboard_font_weight: selectedDashboardFontWeight.value,
  });
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};
const selectDashboardFontSize = async size => {
  selectedDashboardFontSize.value = size;
  await updateFontSize(size);
  window.dispatchEvent(new CustomEvent('appearance-preferences-changed'));
};

const previewNotification = () => {
  const previewItems =
    selectedNotificationStyle.value === 'stacked'
      ? [
          { message: copy.previewMessage, variant: 'info' },
          { message: 'Novo atendimento recebido.', variant: 'success' },
          { message: 'Uma tarefa precisa da sua atenção.', variant: 'warning' },
        ]
      : [{ message: copy.previewMessage, variant: 'info' }];

  previewItems.forEach((item, index) => {
    window.setTimeout(() => {
      emitter.emit('newToastMessage', {
        message: item.message,
        action: { variant: item.variant, duration: 7000 },
      });
    }, index * 90);
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] isolate flex items-start justify-center overflow-hidden bg-black/60 p-3 backdrop-blur-md sm:p-5"
      @click.self="close"
    >
      <section
        class="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl sm:max-h-[calc(100dvh-2.5rem)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="appearance-dialog-title"
      >
        <!-- Header -->
        <header
          class="flex shrink-0 items-center justify-between gap-4 px-5 py-4 sm:px-6"
        >
          <div class="flex items-center gap-3">
            <div
              class="grid size-9 place-content-center rounded-xl bg-n-brand/10"
            >
              <Icon icon="i-lucide-palette" class="size-5 text-n-brand" />
            </div>
            <div>
              <p
                class="text-[10px] font-semibold uppercase tracking-widest text-n-brand"
              >
                {{ copy.eyebrow }}
              </p>
              <h2
                id="appearance-dialog-title"
                class="text-base font-semibold text-n-slate-12"
              >
                {{ copy.title }}
              </h2>
            </div>
          </div>
          <button
            type="button"
            class="grid size-8 place-content-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            :aria-label="copy.close"
            :title="copy.close"
            @click.stop.prevent="close"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </header>

        <nav
          class="flex shrink-0 gap-1.5 overflow-x-auto border-y border-n-weak bg-n-alpha-1 px-5 py-2.5 sm:px-6"
          :aria-label="copy.navigationLabel"
        >
          <button
            v-for="panel in appearancePanels"
            :key="panel.key"
            type="button"
            class="inline-flex min-h-9 items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold transition"
            :class="
              activePanel === panel.key
                ? 'bg-n-solid-1 text-n-brand shadow-sm ring-1 ring-n-weak'
                : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="activePanel = panel.key"
          >
            <Icon :icon="panel.icon" class="size-4" />
            {{ panel.label }}
          </button>
        </nav>

        <div
          ref="contentRef"
          class="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 py-5 sm:px-6"
        >
          <div class="grid gap-5 md:grid-cols-2">
            <section
              v-show="activePanel === 'layout'"
              class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 md:col-span-2"
            >
              <div class="mb-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.layoutTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.layoutDescription }}
                </p>
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <button
                  v-for="option in dashboardLayoutOptions"
                  :key="option.key"
                  type="button"
                  class="group flex items-center gap-4 rounded-2xl border bg-n-solid-1 p-4 text-left transition-all"
                  :class="
                    selectedDashboardLayout === option.key
                      ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/15'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  :aria-pressed="selectedDashboardLayout === option.key"
                  @click="selectedDashboardLayout = option.key"
                >
                  <span
                    class="grid size-12 shrink-0 place-content-center rounded-xl bg-n-alpha-2 text-n-slate-11 transition group-hover:text-n-brand"
                    :class="
                      selectedDashboardLayout === option.key &&
                      'bg-n-brand/10 text-n-brand'
                    "
                  >
                    <Icon :icon="option.icon" class="size-6" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-n-slate-12">
                      {{ option.label }}
                    </span>
                    <span class="block text-xs text-n-slate-10">
                      {{ option.description }}
                    </span>
                  </span>
                  <Icon
                    v-if="selectedDashboardLayout === option.key"
                    icon="i-lucide-circle-check"
                    class="size-5 shrink-0 text-n-brand"
                  />
                </button>
              </div>
              <div
                class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-n-alpha-1 p-3"
              >
                <div>
                  <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                    {{ copy.navigationTitle }}
                  </h4>
                  <p class="m-0 mt-1 text-xs text-n-slate-10">
                    {{ copy.navigationDescription }}
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-medium text-n-slate-12 transition hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  @click="customizeNavigation"
                >
                  <Icon icon="i-lucide-sliders-horizontal" class="size-4" />
                  {{ copy.customizeNavigation }}
                </button>
              </div>
              <p class="mb-0 mt-3 text-[11px] text-n-slate-9 xl:hidden">
                {{ copy.horizontalResponsiveNote }}
              </p>
            </section>

            <section
              v-show="activePanel === 'layout'"
              class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 md:col-span-2"
            >
              <div class="mb-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.containerTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.containerDescription }}
                </p>
              </div>
              <div class="grid gap-3 sm:grid-cols-2">
                <button
                  v-for="option in dashboardContainerOptions"
                  :key="option.key"
                  type="button"
                  class="group flex items-center gap-4 rounded-2xl border bg-n-solid-1 p-4 text-left transition-all"
                  :class="
                    selectedDashboardContainer === option.key
                      ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/15'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  :aria-pressed="selectedDashboardContainer === option.key"
                  @click="selectedDashboardContainer = option.key"
                >
                  <span
                    class="grid size-12 shrink-0 place-content-center rounded-xl bg-n-alpha-2 text-n-slate-11 transition group-hover:text-n-brand"
                    :class="
                      selectedDashboardContainer === option.key &&
                      'bg-n-brand/10 text-n-brand'
                    "
                  >
                    <Icon :icon="option.icon" class="size-6" />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="block text-sm font-semibold text-n-slate-12">
                      {{ option.label }}
                    </span>
                    <span class="block text-xs text-n-slate-10">
                      {{ option.description }}
                    </span>
                  </span>
                  <Icon
                    v-if="selectedDashboardContainer === option.key"
                    icon="i-lucide-circle-check"
                    class="size-5 shrink-0 text-n-brand"
                  />
                </button>
              </div>
            </section>

            <!-- Primary color -->
            <AppearanceAccentColorPicker
              v-show="activePanel === 'tones'"
              v-model="selectedPrimaryColor"
              class="order-3 md:col-span-2"
            />

            <!-- Color mode -->
            <section
              v-show="activePanel === 'tones'"
              class="order-1 rounded-2xl border border-n-weak bg-n-solid-2 p-5 md:col-span-2"
            >
              <div class="mb-4">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.colorModeTitle }}
                </h3>
                <p class="mb-0 mt-1 text-xs text-n-slate-10">
                  {{ copy.colorModeDescription }}
                </p>
              </div>
              <div class="grid grid-cols-3 gap-1 rounded-xl bg-n-alpha-1 p-1">
                <button
                  v-for="option in colorSchemeOptions"
                  :key="option.key"
                  type="button"
                  class="flex min-h-12 items-center justify-center gap-2 rounded-lg px-3 py-2.5 transition-all"
                  :class="
                    selectedColorScheme === option.key
                      ? 'bg-n-solid-1 text-n-brand shadow-sm ring-1 ring-n-weak'
                      : 'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12'
                  "
                  :aria-pressed="selectedColorScheme === option.key"
                  @click="selectedColorScheme = option.key"
                >
                  <Icon :icon="option.icon" class="size-4" />
                  <span class="text-xs font-medium">{{ option.label }}</span>
                </button>
              </div>
            </section>

            <!-- Dashboard font -->
            <section
              v-show="activePanel === 'fonts'"
              class="order-2 rounded-2xl border border-n-weak bg-n-solid-2 p-5 md:col-span-2"
            >
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ copy.fontTitle }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{ copy.fontDescription }}
                </p>
              </div>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <button
                  v-for="font in DASHBOARD_FONT_OPTIONS"
                  :key="font.key"
                  type="button"
                  class="relative flex flex-col items-start gap-1.5 rounded-xl border bg-n-solid-1 p-2.5 text-left transition-all"
                  :class="
                    selectedDashboardFont === font.key
                      ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/15'
                      : 'border-n-weak hover:border-n-slate-7'
                  "
                  :aria-pressed="selectedDashboardFont === font.key"
                  @click="selectDashboardFont(font.key)"
                >
                  <span
                    class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-sm font-semibold text-n-slate-12"
                    :style="{ fontFamily: font.family }"
                  >
                    {{ copy.fontSample }}
                  </span>
                  <span class="min-w-0 max-w-full pr-4">
                    <span
                      class="block truncate text-xs font-semibold text-n-slate-12"
                      :style="{ fontFamily: font.family }"
                    >
                      {{ font.label }}
                    </span>
                  </span>
                  <Icon
                    v-if="selectedDashboardFont === font.key"
                    icon="i-lucide-circle-check"
                    class="absolute right-2.5 top-2.5 size-4 text-n-brand"
                  />
                </button>
              </div>
              <div class="mt-4 border-t border-n-weak pt-4">
                <div class="mb-3">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ copy.fontWeightTitle }}
                  </h4>
                  <p class="text-xs text-n-slate-10">
                    {{ copy.fontWeightDescription }}
                  </p>
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <button
                    v-for="weight in DASHBOARD_FONT_WEIGHT_OPTIONS"
                    :key="weight.key"
                    type="button"
                    class="relative rounded-xl border bg-n-solid-1 px-3 py-2.5 text-left text-sm transition-all"
                    :class="
                      selectedDashboardFontWeight === weight.key
                        ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/15'
                        : 'border-n-weak hover:border-n-slate-7'
                    "
                    :style="{ fontWeight: weight.value }"
                    :aria-pressed="selectedDashboardFontWeight === weight.key"
                    @click="selectDashboardFontWeight(weight.key)"
                  >
                    {{ weight.label }}
                    <Icon
                      v-if="selectedDashboardFontWeight === weight.key"
                      icon="i-lucide-circle-check"
                      class="absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-n-brand"
                    />
                  </button>
                </div>
              </div>
              <div
                class="mt-4 flex flex-col gap-3 border-t border-n-weak pt-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="min-w-0">
                  <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
                    {{ copy.fontSizeTitle }}
                  </h4>
                  <p class="mb-0 mt-1 text-xs text-n-slate-10">
                    {{ copy.fontSizeDescription }}
                  </p>
                </div>
                <label class="min-w-36 shrink-0">
                  <span class="sr-only">{{ copy.fontSizeTitle }}</span>
                  <Select
                    v-model="selectedDashboardFontSize"
                    class="w-full [&>select]:w-full"
                    :options="fontSizeOptions"
                    @update:model-value="selectDashboardFontSize"
                  />
                </label>
              </div>
            </section>

            <AppearanceSchemeGallery
              v-if="!isDarkMode"
              v-show="activePanel === 'tones'"
              :model-value="selectedLightScheme"
              class="order-4 md:col-span-2"
              :title="copy.lightSchemeTitle"
              :description="copy.lightSchemeDescription"
              :options-label="copy.darkSchemeOptions"
              icon="i-lucide-sun-medium"
              :schemes="lightSchemes"
              @update:model-value="selectLightScheme"
            />

            <!-- Dark scheme -->
            <AppearanceSchemeGallery
              v-if="isDarkMode"
              v-show="activePanel === 'tones'"
              :model-value="selectedDarkScheme"
              class="order-5 md:col-span-2"
              :title="copy.darkSchemeTitle"
              :description="copy.darkSchemeDescription"
              :options-label="copy.darkSchemeOptions"
              icon="i-lucide-moon-star"
              :schemes="darkSchemes"
              @update:model-value="selectDarkScheme"
            />

            <!-- Wallpaper -->
            <section
              v-show="activePanel === 'conversation'"
              class="rounded-2xl border border-n-weak bg-n-solid-2 p-4 md:col-span-2"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ copy.contactMenuTitle }}
              </h3>
              <p class="mb-3 mt-1 text-xs leading-5 text-n-slate-10">
                {{ copy.contactMenuDescription }}
              </p>
              <div class="flex flex-wrap gap-2">
                <Button
                  v-for="option in [
                    { compact: false, label: 'Completo' },
                    { compact: true, label: 'Compacto' },
                  ]"
                  :key="option.label"
                  :label="option.label"
                  :variant="
                    (uiSettings.conversation_list_compact === true) ===
                    option.compact
                      ? 'solid'
                      : 'outline'
                  "
                  :aria-pressed="
                    (uiSettings.conversation_list_compact === true) ===
                    option.compact
                  "
                  size="sm"
                  @click="
                    updateUISettings({
                      conversation_list_compact: option.compact,
                    })
                  "
                />
              </div>
              <div class="mt-4 max-w-md" data-testid="contact-menu-preview">
                <p class="mb-2 text-xs text-n-slate-10">
                  {{ copy.contactPreviewTitle }}
                </p>
                <div
                  class="flex min-w-0 gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                >
                  <span
                    class="grid size-9 shrink-0 place-items-center rounded-full bg-n-alpha-2 text-n-slate-11"
                  >
                    <Icon icon="i-lucide-user-round" class="size-5" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="flex min-w-0 items-center gap-2">
                      <span
                        class="min-w-0 flex-1 truncate text-sm font-semibold text-n-slate-12"
                      >
                        {{ copy.contactPreviewName }}
                      </span>
                      <Button
                        v-if="uiSettings.conversation_list_compact === true"
                        variant="link"
                        size="xs"
                        class="shrink-0 !p-0"
                        :label="
                          contactPreviewExpanded
                            ? copy.contactPreviewLess
                            : copy.contactPreviewMore
                        "
                        :aria-expanded="contactPreviewExpanded"
                        @click="
                          contactPreviewExpanded = !contactPreviewExpanded
                        "
                      />
                    </div>
                    <p
                      class="mb-0 mt-1 truncate text-xs text-n-slate-11"
                      :title="copy.contactPreviewMessage"
                    >
                      {{ copy.contactPreviewMessage }}
                    </p>
                    <div
                      v-if="
                        uiSettings.conversation_list_compact !== true ||
                        contactPreviewExpanded
                      "
                      class="mt-2"
                      data-testid="contact-menu-preview-details"
                    >
                      <div
                        class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.6875rem] text-n-slate-10"
                      >
                        <span>{{ copy.contactPreviewInbox }}</span>
                        <span class="inline-flex items-center gap-1">
                          <Icon icon="i-lucide-user-round" class="size-3" />
                          {{ copy.contactPreviewAssignee }}
                        </span>
                      </div>
                      <span
                        class="mt-2 inline-flex items-center rounded border-l-[3px] border-n-amber-9 bg-n-alpha-2 px-1.5 py-1 text-[0.6875rem] text-n-slate-11"
                      >
                        {{ copy.contactPreviewLabel }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <AppearanceWallpaperPicker
              v-show="activePanel === 'conversation'"
              v-model="selectedWallpaper"
              class="md:col-span-2"
              :options="wallpaperOptions"
              :custom-url="customWallpaperUrl"
              :custom-name="customWallpaperName"
              :error="customWallpaperError"
              :busy="isSavingCustomWallpaper"
              @upload="handleWallpaperUpload"
              @remove="removeCustomWallpaper"
            />

            <!-- Notifications -->
            <section
              v-show="activePanel === 'notifications'"
              class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 md:col-span-2"
            >
              <div
                class="flex items-start gap-3 border-b border-n-weak bg-n-alpha-1 px-4 py-3"
              >
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
                >
                  <Icon icon="i-lucide-bell-ring" class="size-4" />
                </span>
                <div class="min-w-0 flex-1">
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    {{ copy.notificationsTitle }}
                  </h3>
                  <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-10">
                    {{ copy.notificationsDescription }}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="faded"
                  color="slate"
                  icon="i-lucide-play"
                  :label="copy.previewNotification"
                  @click="previewNotification"
                />
              </div>
              <div class="grid gap-3 p-4 sm:grid-cols-2">
                <button
                  v-for="style in notificationStyles"
                  :key="style.key"
                  type="button"
                  class="group relative rounded-2xl border p-3 text-left transition-all"
                  :class="
                    selectedNotificationStyle === style.key
                      ? 'border-n-brand bg-n-brand/5 shadow-sm ring-2 ring-n-brand/15'
                      : 'border-n-weak bg-n-solid-1 hover:border-n-strong hover:bg-n-alpha-1'
                  "
                  :aria-pressed="selectedNotificationStyle === style.key"
                  @click="selectedNotificationStyle = style.key"
                >
                  <div
                    class="mb-3 h-24 overflow-hidden rounded-xl border border-n-weak bg-n-background p-3"
                  >
                    <div v-if="style.key === 'stacked'" class="relative h-full">
                      <div
                        class="absolute left-5 right-0 top-1 h-12 rounded-lg border border-n-weak bg-n-solid-1 opacity-45 shadow-sm"
                      />
                      <div
                        class="absolute left-2 right-3 top-3 h-12 rounded-lg border border-n-weak bg-n-solid-1 opacity-70 shadow-sm"
                      />
                      <div
                        class="absolute inset-x-0 bottom-0 flex h-12 items-center gap-2 rounded-lg border border-n-strong bg-n-solid-2 px-2.5 shadow-md"
                      >
                        <span
                          class="grid size-6 shrink-0 place-items-center rounded-md bg-n-blue-3 text-n-blue-10"
                        >
                          <span class="i-lucide-message-circle size-3.5" />
                        </span>
                        <span class="min-w-0 flex-1 space-y-1.5">
                          <span
                            class="block h-1.5 w-2/3 rounded-full bg-n-slate-8"
                          />
                          <span
                            class="block h-1.5 w-full rounded-full bg-n-slate-5"
                          />
                        </span>
                      </div>
                    </div>
                    <div v-else class="grid gap-2">
                      <div
                        v-for="tone in ['blue', 'amber']"
                        :key="tone"
                        class="flex h-8 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-2 shadow-sm"
                      >
                        <span
                          class="size-2 shrink-0 rounded-full"
                          :class="
                            tone === 'blue' ? 'bg-n-blue-9' : 'bg-n-amber-9'
                          "
                        />
                        <span class="min-w-0 flex-1 space-y-1">
                          <span
                            class="block h-1.5 w-1/2 rounded-full bg-n-slate-8"
                          />
                          <span
                            class="block h-1.5 w-4/5 rounded-full bg-n-slate-5"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-start gap-2.5 px-1 pb-1">
                    <span
                      class="grid size-8 shrink-0 place-items-center rounded-lg"
                      :class="
                        selectedNotificationStyle === style.key
                          ? 'bg-n-brand text-white'
                          : 'bg-n-alpha-2 text-n-slate-10 group-hover:text-n-slate-12'
                      "
                    >
                      <Icon :icon="style.icon" class="size-4" />
                    </span>
                    <div class="min-w-0 flex-1">
                      <div class="text-sm font-semibold text-n-slate-12">
                        {{ style.label }}
                      </div>
                      <div class="mt-0.5 text-xs leading-4 text-n-slate-10">
                        {{ style.description }}
                      </div>
                    </div>
                    <Icon
                      :icon="
                        selectedNotificationStyle === style.key
                          ? 'i-lucide-circle-check'
                          : 'i-lucide-circle'
                      "
                      class="mt-1 size-4 shrink-0"
                      :class="
                        selectedNotificationStyle === style.key
                          ? 'text-n-brand'
                          : 'text-n-slate-8'
                      "
                    />
                  </div>
                </button>
              </div>
              <div class="border-t border-n-weak p-4">
                <div class="mb-3">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ copy.notificationPositionTitle }}
                  </h4>
                  <p class="mb-0 mt-0.5 text-xs leading-5 text-n-slate-10">
                    {{ copy.notificationPositionDescription }}
                  </p>
                </div>
                <div
                  class="grid grid-cols-3 gap-2 rounded-xl border border-n-weak bg-n-background p-2"
                >
                  <button
                    v-for="position in notificationPositions"
                    :key="position.key"
                    type="button"
                    class="group flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2 text-center transition"
                    :class="
                      selectedNotificationPosition === position.key
                        ? 'border-n-brand bg-n-brand/10 text-n-brand shadow-sm'
                        : 'border-transparent bg-n-solid-1 text-n-slate-10 hover:border-n-strong hover:text-n-slate-12'
                    "
                    :aria-label="position.label"
                    :aria-pressed="
                      selectedNotificationPosition === position.key
                    "
                    @click="selectedNotificationPosition = position.key"
                  >
                    <Icon :icon="position.icon" class="size-4" />
                    <span class="text-[10px] font-medium leading-3">
                      {{ position.label }}
                    </span>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>

        <footer
          class="flex shrink-0 justify-end border-t border-n-weak px-5 py-3 sm:px-6"
        >
          <Button
            type="button"
            variant="faded"
            color="slate"
            :label="copy.close"
            @click="close"
          />
        </footer>
      </section>
    </div>
  </Teleport>
</template>
